import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import nl from './locales/nl.json';
import it from './locales/it.json';
import pl from './locales/pl.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import es from './locales/es.json';
import ptBR from './locales/pt-BR.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt-BR', name: 'Português', flag: '🇧🇷' },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]['code'];

const CODES = SUPPORTED_LANGUAGES.map((l) => l.code) as readonly string[];

/**
 * Un tag qualsiasi ricondotto a una lingua che esiste davvero qui dentro.
 * Serve per il portoghese: il codice è 'pt-BR', quindi un browser impostato su
 * 'pt' o 'pt-PT' non combacerebbe e finirebbe in inglese. Il ripiego passa per
 * il tag base, non per gli interni di i18next.
 */
export function normalizeLanguage(lng: string | undefined): SupportedLanguage {
  if (lng && CODES.includes(lng)) return lng as SupportedLanguage;
  const base = (lng ?? '').split('-')[0]?.toLowerCase();
  if (!base) return 'en';
  const match = CODES.find((code) => code === base || code.split('-')[0] === base);
  return (match ?? 'en') as SupportedLanguage;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      nl: { translation: nl },
      it: { translation: it },
      pl: { translation: pl },
      de: { translation: de },
      fr: { translation: fr },
      es: { translation: es },
      'pt-BR': { translation: ptBR },
    },
    fallbackLng: { pt: ['pt-BR'], 'pt-PT': ['pt-BR'], default: ['en'] },
    supportedLngs: ['it', 'en', 'nl', 'pl', 'de', 'fr', 'es', 'pt-BR'],
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'draughts-language',
    },
  });

// Il rilevamento può fermarsi su un tag che non abbiamo (tipicamente 'pt' o
// 'pt-PT'): senza questa riconduzione i18n.language resterebbe quel tag, il
// selettore mostrerebbe il ripiego generico e isRulesLang() fallirebbe in
// silenzio, lasciando le pagine regole senza canonical né hreflang.
if (!CODES.includes(i18n.language)) {
  void i18n.changeLanguage(normalizeLanguage(i18n.language));
}

/*
 * `<html lang>` segue la lingua scelta.
 *
 * `index.html` dichiara `lang="en"` e nessuno lo aggiornava mai: né all'avvio né
 * al cambio di lingua. Quel attributo non è decorativo, ed è letto da chi non può
 * controllare quello che legge:
 *   - uno screen reader ci scegli la VOCE e la pronuncia: un testo italiano letto
 *     con le regole inglesi è, letteralmente, incomprensibile;
 *   - il browser ci decide la sillabazione, le virgolette tipografiche e se
 *     proporre la traduzione automatica della pagina;
 *   - i motori di ricerca ci leggono la lingua del documento.
 *
 * Si scrive all'avvio e a ogni `languageChanged` — che è l'evento che i18next
 * emette sia per il cambio dal selettore sia per quello dalle preferenze del
 * profilo, quindi copre tutte e tre le chiamate a `changeLanguage` senza doverle
 * conoscere.
 *
 * ⚠️ E non è solo dichiarativo: `utils/formato-data` legge PROPRIO questo
 * attributo per formattare date e ore nella lingua dell'app (importare l'istanza
 * i18n da lì tirerebbe l'inizializzazione di i18next dentro mezza suite di test).
 * Chi tocca questa riga tocca anche i formati delle date.
 */
function allineaLangDelDocumento(lingua: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = normalizeLanguage(lingua);
}

allineaLangDelDocumento(i18n.language);
i18n.on('languageChanged', allineaLangDelDocumento);

export default i18n;
